import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../utils/api';

const DeploymentToolsPage = () => {
  const { currentUser } = useOutletContext();
  
  // Environment state
  const [environment, setEnvironment] = useState(null);
  const [envLoading, setEnvLoading] = useState(true);

  // Custom script modal state
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [scriptContent, setScriptContent] = useState('');
  const [scriptType, setScriptType] = useState('python');
  const [isRunning, setIsRunning] = useState(false);
  const [scriptOutput, setScriptOutput] = useState('');
  const [exitCode, setExitCode] = useState(null);
  const [outputCopied, setOutputCopied] = useState(false);
  const outputRef = useRef(null);

  // Tool execution states
  const [verifyingStorage, setVerifyingStorage] = useState(false);
  const [storageResult, setStorageResult] = useState(null);
  const [validatingData, setValidatingData] = useState(false);
  const [dataIntegrityResult, setDataIntegrityResult] = useState(null);
  const [refreshingSessions, setRefreshingSessions] = useState(false);
  const [sessionResult, setSessionResult] = useState(null);

  // Database backup modal state
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(null);
  const [deletingBackup, setDeletingBackup] = useState(null);
  const [downloadingBackup, setDownloadingBackup] = useState(null);

  // Sample scripts modal state
  const [showSampleScriptsModal, setShowSampleScriptsModal] = useState(false);

  // Orphaned records modal state
  const [showOrphanedModal, setShowOrphanedModal] = useState(false);
  const [orphanedCollection, setOrphanedCollection] = useState('');
  const [orphanedRecords, setOrphanedRecords] = useState([]);
  const [loadingOrphaned, setLoadingOrphaned] = useState(false);
  const [deletingOrphaned, setDeletingOrphaned] = useState(false);

  // Subscription Simulations modal state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionOrgId, setSubscriptionOrgId] = useState('');
  const [subscriptionOrgData, setSubscriptionOrgData] = useState(null);
  const [validatingOrg, setValidatingOrg] = useState(false);
  const [executingAction, setExecutingAction] = useState(null);

  // Push Token Registry state
  const [showPushTokenModal, setShowPushTokenModal] = useState(false);
  const [pushTokens, setPushTokens] = useState([]);
  const [pushTokenStats, setPushTokenStats] = useState({ total: 0, admins: 0, team_members: 0 });
  const [loadingPushTokens, setLoadingPushTokens] = useState(false);
  const [expandedOrgs, setExpandedOrgs] = useState({});

  // User Factory state
  const [showUserFactoryModal, setShowUserFactoryModal] = useState(false);
  const [userFactoryType, setUserFactoryType] = useState('team_member');
  const [userFactoryOrg, setUserFactoryOrg] = useState('');
  const [userFactoryForm, setUserFactoryForm] = useState({
    first_name: '', middle_name: '', last_name: '', email: '', phone_number: '',
    username: '', password: '', role: '', department: '', staff_id: '',
    date_of_birth: '', address: '', bank_name: '', account_number: '', sort_code: '',
    cis_registered: false, utr_number: '', ni_number: '', gross_hourly_rate: '', net_hourly_rate: ''
  });
  const [userFactoryOrgs, setUserFactoryOrgs] = useState([]);
  const [userFactoryCreating, setUserFactoryCreating] = useState(false);
  const [userFactoryResult, setUserFactoryResult] = useState(null);

  // Fetch environment on mount
  useEffect(() => {
    const fetchEnvironment = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/super-admin/system/environment`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEnvironment(response.data);
      } catch (error) {
        console.error('Failed to fetch environment:', error);
        setEnvironment({ environment: 'development', debug_mode: false });
      } finally {
        setEnvLoading(false);
      }
    };
    
    fetchEnvironment();
  }, []);

  // Auto-scroll output
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [scriptOutput]);

  // Environment badge configuration
  const getEnvironmentConfig = (env) => {
    switch (env?.environment) {
      case 'production':
        return {
          label: 'Production',
          bgColor: 'bg-green-500/20',
          borderColor: 'border-green-500/40',
          textColor: 'text-green-300',
          iconColor: 'text-green-300',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          )
        };
      case 'staging':
        return {
          label: 'Staging',
          bgColor: 'bg-blue-500/20',
          borderColor: 'border-blue-500/40',
          textColor: 'text-blue-300',
          iconColor: 'text-blue-300',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          )
        };
      default:
        return {
          label: 'Development',
          bgColor: 'bg-amber-500/20',
          borderColor: 'border-amber-500/40',
          textColor: 'text-amber-200',
          iconColor: 'text-amber-300',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )
        };
    }
  };

  const envConfig = getEnvironmentConfig(environment);

  // Script modal handlers
  const openScriptModal = () => {
    setScriptContent('');
    setScriptOutput('');
    setExitCode(null);
    setShowScriptModal(true);
  };

  const closeScriptModal = () => {
    if (isRunning) return;
    setShowScriptModal(false);
    setScriptContent('');
    setScriptOutput('');
    setExitCode(null);
  };

  // Run script with streaming
  const runScript = async () => {
    if (!scriptContent.trim() || isRunning) return;

    setIsRunning(true);
    setScriptOutput('');
    setExitCode(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/super-admin/tools/run-script`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          script_content: scriptContent,
          script_type: scriptType
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to execute script');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'output') {
                setScriptOutput(prev => prev + data.content);
              } else if (data.type === 'complete') {
                setExitCode(data.exit_code);
              } else if (data.type === 'error') {
                setScriptOutput(prev => prev + `\nError: ${data.message}`);
              }
            } catch (e) {
              // Ignore parsing errors for incomplete chunks
            }
          }
        }
      }
    } catch (error) {
      setScriptOutput(prev => prev + `\nError: ${error.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  // Verify Storage Connection
  const verifyStorage = async () => {
    setVerifyingStorage(true);
    setStorageResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/super-admin/tools/verify-storage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStorageResult(response.data);
    } catch (error) {
      setStorageResult({ success: false, errors: [error.message] });
    } finally {
      setVerifyingStorage(false);
    }
  };

  // Validate Data Integrity
  const validateDataIntegrity = async () => {
    setValidatingData(true);
    setDataIntegrityResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/super-admin/tools/validate-data-integrity`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDataIntegrityResult(response.data);
    } catch (error) {
      setDataIntegrityResult({ success: false, error: error.message });
    } finally {
      setValidatingData(false);
    }
  };

  // Refresh User Sessions
  const refreshSessions = async () => {
    if (!window.confirm('This will log out ALL users across the platform. Are you sure?')) return;
    
    setRefreshingSessions(true);
    setSessionResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/super-admin/tools/refresh-sessions`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessionResult(response.data);
    } catch (error) {
      setSessionResult({ success: false, error: error.message });
    } finally {
      setRefreshingSessions(false);
    }
  };

  // Database Backup handlers
  const openBackupModal = async () => {
    setShowBackupModal(true);
    await fetchBackups();
  };

  const fetchBackups = async () => {
    setLoadingBackups(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/super-admin/tools/database/backups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Failed to fetch backups:', error);
    } finally {
      setLoadingBackups(false);
    }
  };

  const fetchPushTokens = async () => {
    setLoadingPushTokens(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/super-admin/tools/push-tokens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPushTokens(response.data.tokens || []);
      setPushTokenStats({ total: response.data.total, admins: response.data.admins, team_members: response.data.team_members });
    } catch (error) {
      console.error('Failed to fetch push tokens:', error);
    } finally {
      setLoadingPushTokens(false);
    }
  };

  const fetchUserFactoryOrgs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/super-admin/managed-organisations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserFactoryOrgs(response.data.organisations || response.data || []);
    } catch (error) {
      console.error('Failed to fetch orgs:', error);
    }
  };

  const handleUserFactorySubmit = async () => {
    setUserFactoryCreating(true);
    setUserFactoryResult(null);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...userFactoryForm,
        user_type: userFactoryType,
        organisation_id: userFactoryOrg,
        gross_hourly_rate: userFactoryForm.gross_hourly_rate ? parseFloat(userFactoryForm.gross_hourly_rate) : 0,
        net_hourly_rate: userFactoryForm.net_hourly_rate ? parseFloat(userFactoryForm.net_hourly_rate) : 0,
      };
      const response = await axios.post(`${API}/super-admin/tools/create-user`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserFactoryResult({ success: true, message: response.data.message, username: response.data.username });
      // Reset form
      setUserFactoryForm({
        first_name: '', middle_name: '', last_name: '', email: '', phone_number: '',
        username: '', password: '', role: '', department: '', staff_id: '',
        date_of_birth: '', address: '', bank_name: '', account_number: '', sort_code: '',
        cis_registered: false, utr_number: '', ni_number: '', gross_hourly_rate: '', net_hourly_rate: ''
      });
    } catch (error) {
      setUserFactoryResult({ success: false, message: error.response?.data?.detail || 'Failed to create user' });
    } finally {
      setUserFactoryCreating(false);
    }
  };

  const createBackup = async () => {
    setCreatingBackup(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/super-admin/tools/database/backup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchBackups();
    } catch (error) {
      alert(`Backup failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setCreatingBackup(false);
    }
  };

  const restoreBackup = async (backupName) => {
    if (!window.confirm(`⚠️ WARNING: This will OVERWRITE the current database with the backup from ${backupName}.\n\nThis action cannot be undone. Are you absolutely sure?`)) return;
    
    setRestoringBackup(backupName);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/super-admin/tools/database/restore`, 
        { backup_name: backupName },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      alert('Database restored successfully!');
    } catch (error) {
      alert(`Restore failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setRestoringBackup(null);
    }
  };

  const deleteBackup = async (backupName) => {
    if (!window.confirm(`Delete backup "${backupName}"? This cannot be undone.`)) return;
    
    setDeletingBackup(backupName);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/super-admin/tools/database/backup/${backupName}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchBackups();
    } catch (error) {
      alert(`Delete failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setDeletingBackup(null);
    }
  };

  const downloadBackup = async (backupName) => {
    setDownloadingBackup(backupName);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/super-admin/tools/database/backup/${backupName}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.download_url) {
        // Open the signed URL in a new tab to trigger download
        window.open(response.data.download_url, '_blank');
      }
    } catch (error) {
      alert(`Download failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setDownloadingBackup(null);
    }
  };

  // Orphaned records handlers
  const viewOrphanedRecords = async (collection) => {
    setOrphanedCollection(collection);
    setShowOrphanedModal(true);
    setLoadingOrphaned(true);
    setOrphanedRecords([]);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/super-admin/tools/orphaned-records/${collection}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrphanedRecords(response.data.records || []);
    } catch (error) {
      alert(`Failed to fetch orphaned records: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoadingOrphaned(false);
    }
  };

  const deleteAllOrphanedRecords = async (collection) => {
    if (!window.confirm(`⚠️ WARNING: This will permanently delete ALL orphaned records from "${collection}".\n\nThis action cannot be undone. Are you sure?`)) return;
    
    setDeletingOrphaned(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/super-admin/tools/orphaned-records/delete`,
        { collection },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      alert(`Successfully deleted ${response.data.deleted_count} orphaned records.`);
      setShowOrphanedModal(false);
      // Re-run validation to update the count
      validateDataIntegrity();
    } catch (error) {
      alert(`Delete failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setDeletingOrphaned(false);
    }
  };

  // Subscription Simulations handlers
  const openSubscriptionModal = () => {
    setSubscriptionOrgId('');
    setSubscriptionOrgData(null);
    setShowSubscriptionModal(true);
  };

  const validateOrganisation = async () => {
    if (!subscriptionOrgId.trim()) return;
    
    setValidatingOrg(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/super-admin/subscription-simulation/validate/${subscriptionOrgId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionOrgData(response.data);
    } catch (error) {
      setSubscriptionOrgData({ error: error.response?.data?.detail || 'Organisation not found' });
    } finally {
      setValidatingOrg(false);
    }
  };

  const executeSubscriptionAction = async (action) => {
    if (!subscriptionOrgData || subscriptionOrgData.error) return;
    
    setExecutingAction(action);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/super-admin/subscription-simulation/execute`, {
        organisation_id: subscriptionOrgId,
        action: action
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Re-validate to refresh the data
      await validateOrganisation();
    } catch (error) {
      alert(`Action failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setExecutingAction(null);
    }
  };

  // Tool card component
  const ToolCard = ({ icon, title, description, buttonText = 'Run', onClick, disabled = false, loading = false, variant = 'default', result = null }) => {
    const variantStyles = {
      default: {
        border: 'border-gray-200',
        bg: 'bg-white',
        iconBg: 'bg-gray-100',
        iconText: 'text-gray-600',
        button: 'bg-gray-600 hover:bg-gray-700'
      },
      purple: {
        border: 'border-purple-200',
        bg: 'bg-purple-50',
        iconBg: 'bg-purple-100',
        iconText: 'text-purple-600',
        button: 'bg-purple-600 hover:bg-purple-700'
      },
      blue: {
        border: 'border-blue-200',
        bg: 'bg-blue-50',
        iconBg: 'bg-blue-100',
        iconText: 'text-blue-600',
        button: 'bg-blue-600 hover:bg-blue-700'
      },
      amber: {
        border: 'border-amber-200',
        bg: 'bg-amber-50',
        iconBg: 'bg-amber-100',
        iconText: 'text-amber-600',
        button: 'bg-amber-600 hover:bg-amber-700'
      },
      green: {
        border: 'border-green-200',
        bg: 'bg-green-50',
        iconBg: 'bg-green-100',
        iconText: 'text-green-600',
        button: 'bg-green-600 hover:bg-green-700'
      }
    };

    const styles = variantStyles[variant] || variantStyles.default;

    // Format result for display
    const formatResult = (res) => {
      if (!res) return null;
      
      // Storage verification result
      if (res.bucket_name !== undefined) {
        return (
          <div className="space-y-1">
            <div className="font-medium">✓ Storage Connected</div>
            <div className="text-xs space-y-0.5">
              <div>Bucket: {res.bucket_name}</div>
              <div className="flex gap-3">
                <span>List: {res.can_list ? '✓' : '✗'}</span>
                <span>Read: {res.can_read ? '✓' : '✗'}</span>
                <span>Write: {res.can_write ? '✓' : '✗'}</span>
                <span>Delete: {res.can_delete ? '✓' : '✗'}</span>
              </div>
            </div>
          </div>
        );
      }
      
      // Data integrity result
      if (res.healthy !== undefined) {
        return (
          <div className="space-y-1">
            <div className="font-medium">{res.healthy ? '✓ Database Healthy' : '⚠ Issues Found'}</div>
            <div className="text-xs">
              <div>Database: {res.stats?.total_collections} collections | {res.stats?.total_db_documents?.toLocaleString()} documents</div>
              <div className="text-gray-500">Validated: {res.stats?.collections_checked} key collections ({res.stats?.total_documents?.toLocaleString()} documents)</div>
              {res.issues?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {res.issues.map((issue, i) => (
                    <div key={i} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      <span className="text-amber-800">• {issue.message}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          viewOrphanedRecords(issue.collection);
                        }}
                        className="text-xs px-2 py-0.5 bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors"
                      >
                        View & Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      }
      
      // Session refresh result
      if (res.user_sessions_invalidated !== undefined) {
        return (
          <div className="space-y-1">
            <div className="font-medium">✓ Sessions Refreshed</div>
            <div className="text-xs">
              <div>Team members: {res.user_sessions_invalidated} | Org admins: {res.admin_sessions_invalidated}</div>
              <div className="text-green-600 mt-1">Super admin sessions are not affected</div>
            </div>
          </div>
        );
      }
      
      // Generic result
      return <span>{typeof res === 'object' ? JSON.stringify(res) : res}</span>;
    };

    return (
      <div className={`border rounded-xl p-5 ${styles.border} ${styles.bg} transition-all duration-200 hover:shadow-md`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-lg ${styles.iconBg} ${styles.iconText}`}>
              {icon}
            </div>
            <div className="flex-1">
              <h4 className="text-gray-900 font-semibold text-base mb-1">{title}</h4>
              <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
              {result && (
                <div className={`mt-3 p-3 rounded-lg text-sm ${result.success !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {result.success !== false ? (
                    formatResult(result)
                  ) : (
                    <span>Error: {result.error || result.errors?.join(', ')}</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClick}
            disabled={disabled || loading}
            className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
              disabled || loading
                ? 'bg-gray-400 cursor-not-allowed opacity-50' 
                : styles.button
            }`}
          >
            {loading ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Running...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {buttonText}
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full" data-testid="deployment-tools-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white mb-8 shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Deployment Tools</h1>
              <p className="text-purple-200 text-sm">System maintenance and deployment operations</p>
            </div>
          </div>
          {/* Dynamic Environment Indicator */}
          {envLoading ? (
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-500/20 border border-gray-500/40 rounded-lg">
              <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-gray-300 text-sm font-medium">Loading...</span>
            </div>
          ) : (
            <div 
              className={`flex items-center gap-2 px-4 py-2 ${envConfig.bgColor} border ${envConfig.borderColor} rounded-lg`}
              title={`Environment: ${environment?.environment_raw || 'unknown'}\nVersion: ${environment?.version || 'N/A'}`}
            >
              <span className={envConfig.iconColor}>{envConfig.icon}</span>
              <span className={`${envConfig.textColor} text-sm font-medium`}>{envConfig.label}</span>
              {environment?.debug_mode && (
                <span className="px-1.5 py-0.5 bg-red-500/30 text-red-300 text-xs font-bold rounded">DEBUG</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tools Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Run Custom Script */}
        <ToolCard
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          }
          title="Run Custom Script"
          description="Execute a custom Python or shell script directly on the server. Use for ad-hoc database operations or maintenance tasks."
          buttonText="Open Editor"
          onClick={openScriptModal}
          variant="purple"
        />

        {/* Database Backup/Restore */}
        <ToolCard
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
            </svg>
          }
          title="Database Backup / Restore"
          description="Create database backups and restore from previous snapshots. Essential before running risky operations."
          buttonText="Manage Backups"
          onClick={openBackupModal}
          variant="green"
        />

        {/* Verify Storage Connection */}
        <ToolCard
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
          }
          title="Verify Storage Connection"
          description="Test connectivity to Google Cloud Storage and verify bucket permissions for file uploads and downloads."
          buttonText="Test Connection"
          onClick={verifyStorage}
          loading={verifyingStorage}
          variant="blue"
          result={storageResult}
        />

        {/* Validate Data Integrity */}
        <ToolCard
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          title="Validate Data Integrity"
          description="Run integrity checks on database relationships and identify any orphaned records or data inconsistencies."
          buttonText="Run Validation"
          onClick={validateDataIntegrity}
          loading={validatingData}
          variant="blue"
          result={dataIntegrityResult}
        />

        {/* Refresh User Sessions */}
        <ToolCard
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          }
          title="Refresh User Sessions"
          description="Invalidate all active user sessions, forcing re-authentication. Use after security updates or permission changes."
          buttonText="Refresh Sessions"
          onClick={refreshSessions}
          loading={refreshingSessions}
          variant="amber"
          result={sessionResult}
        />

        {/* Subscription Simulations */}
        <ToolCard
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
            </svg>
          }
          title="Subscription Simulations"
          description="Test and modify organisation subscription states. Reset grace periods, clear PayPal data, and simulate subscription scenarios."
          buttonText="Open Simulator"
          onClick={openSubscriptionModal}
          variant="purple"
        />

        {/* Push Token Registry */}
        <ToolCard
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          }
          title="Push Token Registry"
          description="View all registered mobile push notification tokens and the users they belong to."
          buttonText="View Tokens"
          onClick={() => { setShowPushTokenModal(true); fetchPushTokens(); }}
          variant="blue"
        />

        {/* User Factory */}
        <ToolCard
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          }
          title="User Factory"
          description="Create admin or team member accounts for any organisation. Bypasses email flows and subscription limits."
          buttonText="Create User"
          onClick={() => { setShowUserFactoryModal(true); fetchUserFactoryOrgs(); setUserFactoryResult(null); }}
          variant="green"
        />
      </div>

      {/* Custom Script Modal */}
      {showScriptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[85vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Run Custom Script</h3>
                  <p className="text-purple-200 text-sm">Execute scripts on the server</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {/* Script Type Selector */}
                <div className="flex items-center bg-white/10 rounded-lg p-1">
                  <button
                    onClick={() => setScriptType('python')}
                    disabled={isRunning}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                      scriptType === 'python'
                        ? 'bg-white text-purple-700'
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    Python
                  </button>
                  <button
                    onClick={() => setScriptType('shell')}
                    disabled={isRunning}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                      scriptType === 'shell'
                        ? 'bg-white text-purple-700'
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    Shell
                  </button>
                </div>
                <button
                  onClick={closeScriptModal}
                  disabled={isRunning}
                  className="text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200 disabled:opacity-50"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body - Split View */}
            <div className="flex-1 overflow-hidden flex min-h-0">
              {/* Left: Script Input */}
              <div className="w-1/2 flex flex-col border-r border-gray-200 min-h-0">
                <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                  <span className="text-sm font-medium text-gray-700">
                    {scriptType === 'python' ? 'Python Script' : 'Shell Script'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {scriptContent.split('\n').length} lines
                  </span>
                </div>
                <textarea
                  value={scriptContent}
                  onChange={(e) => setScriptContent(e.target.value)}
                  placeholder={scriptType === 'python' 
                    ? "# Enter your Python script here...\n\nprint('Hello, World!')" 
                    : "# Enter your shell script here...\n\necho 'Hello, World!'"
                  }
                  className="flex-1 px-4 py-3 font-mono text-sm resize-none bg-gray-900 text-green-400 focus:outline-none min-h-0 overflow-auto"
                  spellCheck="false"
                  wrap="off"
                  disabled={isRunning}
                />
              </div>

              {/* Right: Output Display */}
              <div className="w-1/2 flex flex-col min-h-0">
                <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                  <span className="text-sm font-medium text-gray-700">Output</span>
                  <div className="flex items-center gap-2">
                    {exitCode !== null && (
                      <span className={`text-xs px-2 py-0.5 rounded ${exitCode === 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        Exit code: {exitCode}
                      </span>
                    )}
                    {scriptOutput && (
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(scriptOutput);
                          setOutputCopied(true);
                          setTimeout(() => setOutputCopied(false), 2000);
                        }}
                        className={`p-1.5 rounded transition-all duration-200 ${
                          outputCopied 
                            ? 'bg-green-100 text-green-600' 
                            : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'
                        }`}
                        title={outputCopied ? 'Copied!' : 'Copy output'}
                      >
                        {outputCopied ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>
                </div>
                <div 
                  ref={outputRef}
                  className="flex-1 px-4 py-3 font-mono text-sm bg-gray-950 text-gray-300 overflow-auto whitespace-pre min-h-0"
                >
                  {isRunning && !scriptOutput && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Running script...
                    </div>
                  )}
                  {scriptOutput || (!isRunning && <span className="text-gray-600">Output will appear here...</span>)}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <div className="flex items-center gap-3">
                {/* Combined Notes */}
                <div className="flex items-start gap-3 p-3 bg-gray-100 border border-gray-200 rounded-lg">
                  <svg className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-xs text-gray-600 space-y-1">
                    <p><strong className="text-gray-700">Note:</strong> Scripts run with elevated privileges. Create a backup first for risky operations.</p>
                    <p><strong className="text-gray-700">Tip:</strong> Use Shell with <code className="bg-gray-200 px-1 rounded text-gray-800">mongosh</code> for database queries. Use Python for basic system tasks.</p>
                  </div>
                </div>
                {/* Sample Scripts Button */}
                <button
                  onClick={() => setShowSampleScriptsModal(true)}
                  className="px-4 py-2 text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Sample Scripts
                </button>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setScriptContent('');
                    setScriptOutput('');
                    setExitCode(null);
                  }}
                  disabled={isRunning}
                  className="px-5 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  Clear
                </button>
                <button
                  onClick={runScript}
                  disabled={isRunning || !scriptContent.trim()}
                  className={`px-5 py-2 rounded-lg text-white font-medium transition-all duration-200 flex items-center gap-2 ${
                    isRunning || !scriptContent.trim()
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800'
                  }`}
                >
                  {isRunning ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Running...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Run Script
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Database Backup Modal */}
      {showBackupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Database Backup / Restore</h3>
                  <p className="text-green-200 text-sm">Manage database snapshots</p>
                </div>
              </div>
              <button
                onClick={() => setShowBackupModal(false)}
                className="text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Create Backup Button */}
              <div className="mb-6">
                <button
                  onClick={createBackup}
                  disabled={creatingBackup}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
                >
                  {creatingBackup ? (
                    <>
                      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating Backup...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Create New Backup
                    </>
                  )}
                </button>
              </div>

              {/* Backups List */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Available Backups</h4>
                  {backups.length > 0 && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                        {backups.filter(b => b.backup_type === 'manual').length} Manual
                      </span>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                        {backups.filter(b => b.backup_type === 'auto').length} Auto
                      </span>
                      {backups.filter(b => b.backup_type === 'legacy').length > 0 && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                          {backups.filter(b => b.backup_type === 'legacy').length} Legacy
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mb-3">Automatic backups run daily at 00:00 UTC (keeps 20 most recent)</p>
                {loadingBackups ? (
                  <div className="flex items-center justify-center py-8">
                    <svg className="animate-spin w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : backups.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                    </svg>
                    <p>No backups found</p>
                    <p className="text-sm">Create your first backup above</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {backups.map((backup) => (
                      <div key={backup.name} className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          {/* Backup type badge */}
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            backup.backup_type === 'auto' 
                              ? 'bg-blue-100 text-blue-700' 
                              : backup.backup_type === 'manual'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {backup.backup_type === 'auto' ? 'Auto' : backup.backup_type === 'manual' ? 'Manual' : 'Legacy'}
                          </span>
                          <div>
                            <p className="font-medium text-gray-900">{backup.name}</p>
                            <p className="text-sm text-gray-500">
                              {backup.size_mb} MB • {new Date(backup.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => downloadBackup(backup.name)}
                            disabled={downloadingBackup === backup.name}
                            className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center gap-1"
                            title="Download backup"
                          >
                            {downloadingBackup === backup.name ? (
                              'Preparing...'
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Download
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => restoreBackup(backup.name)}
                            disabled={restoringBackup === backup.name}
                            className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                          >
                            {restoringBackup === backup.name ? 'Restoring...' : 'Restore'}
                          </button>
                          <button
                            onClick={() => deleteBackup(backup.name)}
                            disabled={deletingBackup === backup.name}
                            className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                          >
                            {deletingBackup === backup.name ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="text-amber-800 text-xs">Restoring a backup will OVERWRITE all current data. This action cannot be undone.</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sample Scripts Modal */}
      {showSampleScriptsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Sample Scripts</h3>
                  <p className="text-purple-200 text-sm">Click on any script to copy it to the editor</p>
                </div>
              </div>
              <button
                onClick={() => setShowSampleScriptsModal(false)}
                className="text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Shell Scripts Section */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-green-100 text-green-600 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </span>
                  Shell Scripts
                </h4>
                <div className="space-y-3">
                  {/* System Info */}
                  <div 
                    className="p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 hover:border-purple-300 transition-all"
                    onClick={() => {
                      setScriptType('shell');
                      setScriptContent(`# System Information
echo "=== System Info ==="
uname -a
echo ""
echo "=== Disk Usage ==="
df -h | head -5
echo ""
echo "=== Memory Usage ==="
free -h`);
                      setShowSampleScriptsModal(false);
                    }}
                  >
                    <h5 className="font-medium text-gray-900">System Information</h5>
                    <p className="text-sm text-gray-600 mt-1">Display system details, disk usage, and memory status</p>
                    <code className="text-xs text-gray-500 mt-2 block">uname, df, free</code>
                  </div>

                  {/* List Collections */}
                  <div 
                    className="p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 hover:border-purple-300 transition-all"
                    onClick={() => {
                      setScriptType('shell');
                      setScriptContent(`# List all database collections with document counts
mongosh "$MONGO_URL" --quiet --eval "
  db = db.getSiblingDB('siteorbit');
  print('=== Database Collections ===\\n');
  db.getCollectionNames().sort().forEach(function(c) {
    var count = db[c].countDocuments({});
    print('  ' + c + ': ' + count + ' documents');
  });
"`);
                      setShowSampleScriptsModal(false);
                    }}
                  >
                    <h5 className="font-medium text-gray-900">List Database Collections</h5>
                    <p className="text-sm text-gray-600 mt-1">Show all collections and their document counts</p>
                    <code className="text-xs text-gray-500 mt-2 block">mongosh</code>
                  </div>

                  {/* Count Users by Org */}
                  <div 
                    className="p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 hover:border-purple-300 transition-all"
                    onClick={() => {
                      setScriptType('shell');
                      setScriptContent(`# Count users per organisation
mongosh "$MONGO_URL" --quiet --eval "
  db = db.getSiblingDB('siteorbit');
  print('=== Users per Organisation ===\\n');
  db.managed_organisations.find({}, {name: 1, organisation_id: 1}).forEach(function(org) {
    var count = db.users.countDocuments({organisation_id: org.organisation_id});
    print('  ' + org.name + ': ' + count + ' users');
  });
"`);
                      setShowSampleScriptsModal(false);
                    }}
                  >
                    <h5 className="font-medium text-gray-900">Users per Organisation</h5>
                    <p className="text-sm text-gray-600 mt-1">Count team members in each organisation</p>
                    <code className="text-xs text-gray-500 mt-2 block">mongosh aggregation</code>
                  </div>

                  {/* Find User by Email */}
                  <div 
                    className="p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 hover:border-purple-300 transition-all"
                    onClick={() => {
                      setScriptType('shell');
                      setScriptContent(`# Find a user by email (replace EMAIL below)
EMAIL="user@example.com"

mongosh "$MONGO_URL" --quiet --eval "
  db = db.getSiblingDB('siteorbit');
  var user = db.users.findOne({email: '$EMAIL'});
  if (user) {
    print('=== User Found ===');
    print('Name: ' + user.first_name + ' ' + user.last_name);
    print('Email: ' + user.email);
    print('Role: ' + user.role);
    print('Department: ' + user.department);
    print('Organisation: ' + user.organisation_name);
    print('Active: ' + (user.active ? 'Yes' : 'No'));
    print('ID: ' + user.id);
  } else {
    print('User not found with email: $EMAIL');
  }
"`);
                      setShowSampleScriptsModal(false);
                    }}
                  >
                    <h5 className="font-medium text-gray-900">Find User by Email</h5>
                    <p className="text-sm text-gray-600 mt-1">Look up a specific user's details by email address</p>
                    <code className="text-xs text-gray-500 mt-2 block">mongosh findOne</code>
                  </div>

                  {/* Recent Clock Records */}
                  <div 
                    className="p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 hover:border-purple-300 transition-all"
                    onClick={() => {
                      setScriptType('shell');
                      setScriptContent(`# Show recent clock records (last 10)
mongosh "$MONGO_URL" --quiet --eval "
  db = db.getSiblingDB('siteorbit');
  print('=== Recent Clock Records ===\\n');
  db.clock_records.find({}).sort({clock_in_time: -1}).limit(10).forEach(function(r) {
    var user = db.users.findOne({id: r.user_id});
    var site = db.sites.findOne({id: r.site_id});
    var name = user ? (user.first_name + ' ' + user.last_name) : 'Unknown';
    var siteName = site ? site.site_name : r.site_id;
    var dateStr = r.clock_in_time ? r.clock_in_time.toISOString().split('T')[0] : 'No date';
    var status = r.is_active ? 'CLOCKED IN' : 'Completed';
    print(dateStr + ' | ' + name + ' | ' + siteName + ' | ' + status);
  });
"`);
                      setShowSampleScriptsModal(false);
                    }}
                  >
                    <h5 className="font-medium text-gray-900">Recent Clock Records</h5>
                    <p className="text-sm text-gray-600 mt-1">Display the 10 most recent clock in/out records</p>
                    <code className="text-xs text-gray-500 mt-2 block">mongosh sort/limit</code>
                  </div>

                  {/* Database Stats */}
                  <div 
                    className="p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 hover:border-purple-300 transition-all"
                    onClick={() => {
                      setScriptType('shell');
                      setScriptContent(`# Database statistics
mongosh "$MONGO_URL" --quiet --eval "
  db = db.getSiblingDB('siteorbit');
  var stats = db.stats();
  print('=== Database Statistics ===\\n');
  print('Database: ' + stats.db);
  print('Collections: ' + stats.collections);
  print('Documents: ' + stats.objects);
  print('Data Size: ' + (stats.dataSize / 1024 / 1024).toFixed(2) + ' MB');
  print('Storage Size: ' + (stats.storageSize / 1024 / 1024).toFixed(2) + ' MB');
  print('Indexes: ' + stats.indexes);
"`);
                      setShowSampleScriptsModal(false);
                    }}
                  >
                    <h5 className="font-medium text-gray-900">Database Statistics</h5>
                    <p className="text-sm text-gray-600 mt-1">Show overall database size and statistics</p>
                    <code className="text-xs text-gray-500 mt-2 block">mongosh db.stats()</code>
                  </div>
                </div>
              </div>

              {/* Python Scripts Section */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                  </span>
                  Python Scripts
                  <span className="text-xs text-gray-500 font-normal">(stdlib only - no external packages)</span>
                </h4>
                <div className="space-y-3">
                  {/* Python Environment */}
                  <div 
                    className="p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 hover:border-purple-300 transition-all"
                    onClick={() => {
                      setScriptType('python');
                      setScriptContent(`# Python environment information
import os
import sys
import datetime

print("=== Python Environment ===")
print(f"Python version: {sys.version}")
print(f"Working directory: {os.getcwd()}")
print(f"Current time: {datetime.datetime.now()}")
print(f"\\n=== Key Environment Variables ===")
print(f"MONGO_URL: {'Set' if os.environ.get('MONGO_URL') else 'Not set'}")
print(f"DB_NAME: {os.environ.get('DB_NAME', 'Not set')}")`);
                      setShowSampleScriptsModal(false);
                    }}
                  >
                    <h5 className="font-medium text-gray-900">Python Environment</h5>
                    <p className="text-sm text-gray-600 mt-1">Display Python version and environment details</p>
                    <code className="text-xs text-gray-500 mt-2 block">os, sys, datetime</code>
                  </div>

                  {/* List Files */}
                  <div 
                    className="p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 hover:border-purple-300 transition-all"
                    onClick={() => {
                      setScriptType('python');
                      setScriptContent(`# List files in a directory
import os

directory = "/app/backend"
print(f"=== Files in {directory} ===\\n")

for item in sorted(os.listdir(directory)):
    path = os.path.join(directory, item)
    if os.path.isdir(path):
        print(f"  [DIR]  {item}/")
    else:
        size = os.path.getsize(path)
        print(f"  [FILE] {item} ({size:,} bytes)")`);
                      setShowSampleScriptsModal(false);
                    }}
                  >
                    <h5 className="font-medium text-gray-900">List Directory Contents</h5>
                    <p className="text-sm text-gray-600 mt-1">Show files and folders in a directory</p>
                    <code className="text-xs text-gray-500 mt-2 block">os.listdir</code>
                  </div>

                  {/* JSON Processing */}
                  <div 
                    className="p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 hover:border-purple-300 transition-all"
                    onClick={() => {
                      setScriptType('python');
                      setScriptContent(`# Read and display environment variables
import os

env_file = "/app/backend/.env"  # Change to /app/frontend/.env for frontend

print("=== Environment Variables ===\\n")
if os.path.exists(env_file):
    with open(env_file, 'r') as f:
        for line in f:
            line = line.strip()
            if line and not line.startswith('#') and '=' in line:
                key, value = line.split('=', 1)
                print(f"  {key} = {value}")
else:
    print("  .env file not found")`);
                      setShowSampleScriptsModal(false);
                    }}
                  >
                    <h5 className="font-medium text-gray-900">Check Environment Keys</h5>
                    <p className="text-sm text-gray-600 mt-1">List all environment variables with their values</p>
                    <code className="text-xs text-gray-500 mt-2 block">file I/O</code>
                  </div>

                  {/* Update Environment Key */}
                  <div 
                    className="p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 hover:border-purple-300 transition-all"
                    onClick={() => {
                      setScriptType('python');
                      setScriptContent(`# Update an environment variable value
# ⚠️ IMPORTANT: Replace KEY_NAME and NEW_VALUE below before running!

KEY_NAME = "YOUR_KEY_NAME"      # e.g., "APP_ENV", "DEBUG"
NEW_VALUE = "your_new_value"    # e.g., "production", "true"
ENV_FILE = "/app/backend/.env"  # Change to /app/frontend/.env for frontend

import os

def update_env_key(filepath, key, value):
    if not os.path.exists(filepath):
        print(f"❌ File not found: {filepath}")
        return False
    
    # Read current content
    with open(filepath, 'r') as f:
        lines = f.readlines()
    
    # Find and update the key
    key_found = False
    updated_lines = []
    for line in lines:
        stripped = line.strip()
        if stripped and not stripped.startswith('#') and '=' in stripped:
            current_key = stripped.split('=')[0]
            if current_key == key:
                updated_lines.append(f"{key}={value}\\n")
                key_found = True
                print(f"✅ Updated: {key}")
                continue
        updated_lines.append(line)
    
    # If key not found, add it
    if not key_found:
        updated_lines.append(f"{key}={value}\\n")
        print(f"✅ Added new key: {key}")
    
    # Write back
    with open(filepath, 'w') as f:
        f.writelines(updated_lines)
    
    print(f"\\n📁 File saved: {filepath}")
    print(f"⚠️  Restart the service for changes to take effect:")
    print(f"    sudo supervisorctl restart backend")
    return True

# Run the update
print(f"=== Updating Environment Variable ===\\n")
print(f"File: {ENV_FILE}")
print(f"Key: {KEY_NAME}")
print(f"Value: {NEW_VALUE}")
print()

if KEY_NAME == "YOUR_KEY_NAME":
    print("❌ Please edit KEY_NAME and NEW_VALUE before running!")
else:
    update_env_key(ENV_FILE, KEY_NAME, NEW_VALUE)`);
                      setShowSampleScriptsModal(false);
                    }}
                  >
                    <h5 className="font-medium text-gray-900">Update Environment Key</h5>
                    <p className="text-sm text-gray-600 mt-1">Change or add an environment variable value</p>
                    <code className="text-xs text-gray-500 mt-2 block">file I/O, .env update</code>
                  </div>

                  {/* Calculate Date */}
                  <div 
                    className="p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 hover:border-purple-300 transition-all"
                    onClick={() => {
                      setScriptType('python');
                      setScriptContent(`# Date calculations
from datetime import datetime, timedelta

now = datetime.now()
print("=== Date Calculations ===\\n")
print(f"Current time: {now.strftime('%Y-%m-%d %H:%M:%S')}")
print(f"Yesterday: {(now - timedelta(days=1)).strftime('%Y-%m-%d')}")
print(f"Last week: {(now - timedelta(weeks=1)).strftime('%Y-%m-%d')}")
print(f"Last month (30 days): {(now - timedelta(days=30)).strftime('%Y-%m-%d')}")
print(f"\\n=== Week Info ===")
print(f"Day of week: {now.strftime('%A')}")
print(f"Week number: {now.strftime('%W')}")
print(f"Days until end of month: {(datetime(now.year, now.month + 1, 1) - now).days if now.month < 12 else (datetime(now.year + 1, 1, 1) - now).days}")`);
                      setShowSampleScriptsModal(false);
                    }}
                  >
                    <h5 className="font-medium text-gray-900">Date Calculations</h5>
                    <p className="text-sm text-gray-600 mt-1">Useful date/time calculations and formatting</p>
                    <code className="text-xs text-gray-500 mt-2 block">datetime, timedelta</code>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <p className="text-xs text-gray-500 text-center">
                Click any script to load it into the editor. You can modify it before running.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Orphaned Records Modal */}
      {showOrphanedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-amber-600 to-amber-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Orphaned Records</h3>
                  <p className="text-amber-200 text-sm">Collection: {orphanedCollection}</p>
                </div>
              </div>
              <button
                onClick={() => setShowOrphanedModal(false)}
                className="text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingOrphaned ? (
                <div className="flex items-center justify-center py-12">
                  <svg className="animate-spin w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : orphanedRecords.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p>No orphaned records found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 mb-4">
                    Found {orphanedRecords.length} orphaned record(s). These records reference data that no longer exists.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-100">
                          {orphanedRecords[0] && Object.keys(orphanedRecords[0]).map(key => (
                            <th key={key} className="px-3 py-2 text-left font-medium text-gray-700 border-b">
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {orphanedRecords.map((record, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            {Object.values(record).map((value, j) => (
                              <td key={j} className="px-3 py-2 border-b text-gray-600 max-w-xs truncate">
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
              <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                <svg className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="text-red-800 text-xs">Deleting orphaned records is permanent and cannot be undone.</span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowOrphanedModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Close
                </button>
                {orphanedRecords.length > 0 && (
                  <button
                    onClick={() => deleteAllOrphanedRecords(orphanedCollection)}
                    disabled={deletingOrphaned}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    {deletingOrphaned ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete All ({orphanedRecords.length})
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Simulations Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Subscription Simulations</h3>
                  <p className="text-purple-200 text-sm">Test and modify subscription states</p>
                </div>
              </div>
              <button
                onClick={() => setShowSubscriptionModal(false)}
                className="text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Organisation ID Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Organisation ID</label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={subscriptionOrgId}
                    onChange={(e) => setSubscriptionOrgId(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && validateOrganisation()}
                    placeholder="e.g., SO-ORG-XXXX-XX-XX-XXXX"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    data-testid="subscription-org-id-input"
                  />
                  <button
                    onClick={validateOrganisation}
                    disabled={!subscriptionOrgId.trim() || validatingOrg}
                    className="px-5 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                    data-testid="validate-org-btn"
                  >
                    {validatingOrg ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Validating...
                      </>
                    ) : (
                      'Validate'
                    )}
                  </button>
                </div>
              </div>

              {/* Organisation Info Display */}
              {subscriptionOrgData && (
                <div className="mb-6">
                  {subscriptionOrgData.error ? (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                      {subscriptionOrgData.error}
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-900">{subscriptionOrgData.company_name}</h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          subscriptionOrgData.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {subscriptionOrgData.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Subscription Tier:</span>
                          <span className="ml-2 font-medium text-gray-900">{subscriptionOrgData.subscription_tier || '—'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Status:</span>
                          <span className={`ml-2 font-medium ${
                            subscriptionOrgData.subscription_status === 'ACTIVE' ? 'text-green-600' :
                            subscriptionOrgData.subscription_status === 'GRACE_PERIOD' ? 'text-amber-600' :
                            subscriptionOrgData.subscription_status === 'GRACE_EXPIRED' ? 'text-red-600' :
                            subscriptionOrgData.subscription_status === 'CANCELLED' ? 'text-gray-600' :
                            'text-gray-900'
                          }`}>{subscriptionOrgData.subscription_status || '—'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Grace Period Start:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {subscriptionOrgData.grace_period_start 
                              ? new Date(subscriptionOrgData.grace_period_start).toLocaleDateString() 
                              : '—'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">PayPal Subscription ID:</span>
                          <span className="ml-2 font-medium text-gray-900 text-xs">
                            {subscriptionOrgData.paypal_subscription_id || '—'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Grace Reminders Sent:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {subscriptionOrgData.grace_reminders_sent?.length || 0}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Package ID:</span>
                          <span className="ml-2 font-medium text-gray-900 text-xs">
                            {subscriptionOrgData.subscription_package_id || '—'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Actions</h4>
                <p className="text-xs text-red-600 mb-3">All actions require subscription status to be CANCELLED (from the Subscriptions Page on paypal.com) or GRACE_PERIOD</p>
                
                {/* Action 1: Clear Subscription Status */}
                <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Clear Subscription Status</p>
                    <p className="text-sm text-gray-500">Changes subscription_status to GRACE_PERIOD</p>
                  </div>
                  <button
                    onClick={() => executeSubscriptionAction('clear_status')}
                    disabled={
                      !subscriptionOrgData || 
                      subscriptionOrgData.error || 
                      (subscriptionOrgData.subscription_status !== 'CANCELLED' && subscriptionOrgData.subscription_status !== 'GRACE_PERIOD') ||
                      executingAction === 'clear_status'
                    }
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                    data-testid="action-clear-status"
                  >
                    {executingAction === 'clear_status' ? 'Executing...' : 'Execute'}
                  </button>
                </div>

                {/* Action 2: Reset Grace Period */}
                <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Reset Grace Period</p>
                    <p className="text-sm text-gray-500">Sets grace_period_start to today's date</p>
                  </div>
                  <button
                    onClick={() => executeSubscriptionAction('reset_grace_period')}
                    disabled={
                      !subscriptionOrgData || 
                      subscriptionOrgData.error ||
                      (subscriptionOrgData.subscription_status !== 'CANCELLED' && subscriptionOrgData.subscription_status !== 'GRACE_PERIOD') ||
                      executingAction === 'reset_grace_period'
                    }
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                    data-testid="action-reset-grace"
                  >
                    {executingAction === 'reset_grace_period' ? 'Executing...' : 'Execute'}
                  </button>
                </div>

                {/* Action 3: End Grace Period - REMOVED as it doesn't apply to CANCELLED status */}

                {/* Action 4: Reset Grace Period Reminders */}
                <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Reset Grace Period Reminders</p>
                    <p className="text-sm text-gray-500">Clears the grace_reminders_sent field</p>
                  </div>
                  <button
                    onClick={() => executeSubscriptionAction('reset_reminders')}
                    disabled={
                      !subscriptionOrgData || 
                      subscriptionOrgData.error ||
                      (subscriptionOrgData.subscription_status !== 'CANCELLED' && subscriptionOrgData.subscription_status !== 'GRACE_PERIOD') ||
                      !(subscriptionOrgData.grace_reminders_sent?.length > 0) ||
                      executingAction === 'reset_reminders'
                    }
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                    data-testid="action-reset-reminders"
                  >
                    {executingAction === 'reset_reminders' ? 'Executing...' : 'Execute'}
                  </button>
                </div>

                {/* Action 5: Clear Subscription Package ID */}
                <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Clear Subscription Package ID</p>
                    <p className="text-sm text-gray-500">Clears the subscription_package_id field</p>
                  </div>
                  <button
                    onClick={() => executeSubscriptionAction('clear_package_id')}
                    disabled={
                      !subscriptionOrgData || 
                      subscriptionOrgData.error ||
                      (subscriptionOrgData.subscription_status !== 'CANCELLED' && subscriptionOrgData.subscription_status !== 'GRACE_PERIOD') ||
                      !subscriptionOrgData.subscription_package_id ||
                      executingAction === 'clear_package_id'
                    }
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                    data-testid="action-clear-package-id"
                  >
                    {executingAction === 'clear_package_id' ? 'Executing...' : 'Execute'}
                  </button>
                </div>

                {/* Action 6: Clear PayPal Subscription ID */}
                <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Clear PayPal Subscription ID</p>
                    <p className="text-sm text-gray-500">Clears the paypal_subscription_id field</p>
                  </div>
                  <button
                    onClick={() => executeSubscriptionAction('clear_paypal_id')}
                    disabled={
                      !subscriptionOrgData || 
                      subscriptionOrgData.error ||
                      (subscriptionOrgData.subscription_status !== 'CANCELLED' && subscriptionOrgData.subscription_status !== 'GRACE_PERIOD') ||
                      !subscriptionOrgData.paypal_subscription_id ||
                      executingAction === 'clear_paypal_id'
                    }
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                    data-testid="action-clear-paypal-id"
                  >
                    {executingAction === 'clear_paypal_id' ? 'Executing...' : 'Execute'}
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="text-amber-800 text-xs">These actions modify live organisation data. Use with caution in production.</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Push Token Registry Modal */}
      {showPushTokenModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Push Token Registry</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {pushTokenStats.total} registered token{pushTokenStats.total !== 1 ? 's' : ''} ({pushTokenStats.admins} admin{pushTokenStats.admins !== 1 ? 's' : ''}, {pushTokenStats.team_members} team member{pushTokenStats.team_members !== 1 ? 's' : ''})
                  </p>
                </div>
                <button
                  onClick={() => setShowPushTokenModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  data-testid="close-push-token-modal"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {loadingPushTokens ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-3 text-gray-500">Loading tokens...</span>
                </div>
              ) : pushTokens.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>No active push tokens registered.</p>
                </div>
              ) : (() => {
                // Group tokens by organisation
                const grouped = {};
                pushTokens.forEach(t => {
                  const orgKey = t.organisation_id || 'unknown';
                  if (!grouped[orgKey]) {
                    grouped[orgKey] = { name: t.organisation, tokens: [] };
                  }
                  grouped[orgKey].tokens.push(t);
                });

                return (
                  <div className="space-y-3">
                    {Object.entries(grouped).map(([orgId, group]) => (
                      <div key={orgId} className="border border-gray-200 rounded-lg overflow-hidden">
                        <button
                          onClick={() => setExpandedOrgs(prev => ({ ...prev, [orgId]: !prev[orgId] }))}
                          className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <span className="font-semibold text-gray-900">{group.name}</span>
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                              {group.tokens.length} token{group.tokens.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <svg
                            className={`w-4 h-4 text-gray-500 transition-transform ${expandedOrgs[orgId] ? 'rotate-180' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {expandedOrgs[orgId] && (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-t border-gray-200 bg-gray-50/50">
                                  <th className="text-left py-2 px-4 font-medium text-gray-500 text-xs uppercase">User</th>
                                  <th className="text-left py-2 px-4 font-medium text-gray-500 text-xs uppercase">Type</th>
                                  <th className="text-left py-2 px-4 font-medium text-gray-500 text-xs uppercase">Token</th>
                                  <th className="text-left py-2 px-4 font-medium text-gray-500 text-xs uppercase">Last Updated</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.tokens.map((token, idx) => (
                                  <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                                    <td className="py-2.5 px-4">
                                      <div className="font-medium text-gray-900 text-sm">{token.name}</div>
                                      <div className="text-xs text-gray-400">{token.username}</div>
                                    </td>
                                    <td className="py-2.5 px-4">
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                        token.user_type === 'admin'
                                          ? 'bg-purple-100 text-purple-700'
                                          : 'bg-blue-100 text-blue-700'
                                      }`}>
                                        {token.user_type === 'admin' ? 'Admin' : 'Team'}
                                      </span>
                                    </td>
                                    <td className="py-2.5 px-4">
                                      <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono text-gray-600 block max-w-[220px] truncate">
                                        {token.push_token}
                                      </code>
                                    </td>
                                    <td className="py-2.5 px-4 text-gray-500 text-xs whitespace-nowrap">
                                      {token.token_updated_at
                                        ? new Date(token.token_updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                        : 'Unknown'
                                      }
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0 flex justify-between items-center">
              <p className="text-xs text-gray-500">Tokens are registered when users log in via the mobile app.</p>
              <button
                onClick={fetchPushTokens}
                disabled={loadingPushTokens}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}
      {/* User Factory Modal */}
      {showUserFactoryModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">User Factory</h2>
                  <p className="text-sm text-gray-500 mt-1">Create users without email verification flows</p>
                </div>
                <button onClick={() => setShowUserFactoryModal(false)} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="close-user-factory-modal">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Result message */}
              {userFactoryResult && (
                <div className={`p-3 rounded-lg text-sm ${userFactoryResult.success ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  {userFactoryResult.message}
                </div>
              )}

              {/* User type toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => setUserFactoryType('team_member')}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${userFactoryType === 'team_member' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  data-testid="user-type-team"
                >
                  Team Member
                </button>
                <button
                  onClick={() => setUserFactoryType('admin')}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${userFactoryType === 'admin' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  data-testid="user-type-admin"
                >
                  Admin
                </button>
              </div>

              {/* Organisation selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organisation *</label>
                <select
                  value={userFactoryOrg}
                  onChange={(e) => setUserFactoryOrg(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                  data-testid="user-factory-org"
                >
                  <option value="">Select organisation...</option>
                  {userFactoryOrgs.map(o => (
                    <option key={o.organisation_id || o.id} value={o.organisation_id}>
                      {o.name || o.organisation_name || o.organisation_id}
                    </option>
                  ))}
                </select>
              </div>

              {/* Core fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">First Name *</label>
                  <input type="text" value={userFactoryForm.first_name} onChange={(e) => setUserFactoryForm(f => ({...f, first_name: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="uf-first-name" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Last Name *</label>
                  <input type="text" value={userFactoryForm.last_name} onChange={(e) => setUserFactoryForm(f => ({...f, last_name: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="uf-last-name" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                  <input type="email" value={userFactoryForm.email} onChange={(e) => setUserFactoryForm(f => ({...f, email: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="uf-email" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Password *</label>
                  <input type="text" value={userFactoryForm.password} onChange={(e) => setUserFactoryForm(f => ({...f, password: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Set password" data-testid="uf-password" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Username (auto-generated if blank)</label>
                  <input type="text" value={userFactoryForm.username} onChange={(e) => setUserFactoryForm(f => ({...f, username: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. r.jones" data-testid="uf-username" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Phone Number</label>
                  <input type="text" value={userFactoryForm.phone_number} onChange={(e) => setUserFactoryForm(f => ({...f, phone_number: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="uf-phone" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                  <input type="text" value={userFactoryForm.role} onChange={(e) => setUserFactoryForm(f => ({...f, role: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. Site Manager" data-testid="uf-role" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Department</label>
                  <input type="text" value={userFactoryForm.department} onChange={(e) => setUserFactoryForm(f => ({...f, department: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. Operations" data-testid="uf-department" />
                </div>
              </div>

              {/* Team member extra fields */}
              {userFactoryType === 'team_member' && (
                <>
                  <div className="border-t border-gray-200 pt-3 mt-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Additional Details</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Staff ID</label>
                      <input type="text" value={userFactoryForm.staff_id} onChange={(e) => setUserFactoryForm(f => ({...f, staff_id: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Date of Birth</label>
                      <input type="date" value={userFactoryForm.date_of_birth} onChange={(e) => setUserFactoryForm(f => ({...f, date_of_birth: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                    <input type="text" value={userFactoryForm.address} onChange={(e) => setUserFactoryForm(f => ({...f, address: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Gross Hourly Rate (£)</label>
                      <input type="number" step="0.5" value={userFactoryForm.gross_hourly_rate} onChange={(e) => setUserFactoryForm(f => ({...f, gross_hourly_rate: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Net Hourly Rate (£)</label>
                      <input type="number" step="0.5" value={userFactoryForm.net_hourly_rate} onChange={(e) => setUserFactoryForm(f => ({...f, net_hourly_rate: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">NI Number</label>
                      <input type="text" value={userFactoryForm.ni_number} onChange={(e) => setUserFactoryForm(f => ({...f, ni_number: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">UTR Number</label>
                      <input type="text" value={userFactoryForm.utr_number} onChange={(e) => setUserFactoryForm(f => ({...f, utr_number: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Bank Name</label>
                      <input type="text" value={userFactoryForm.bank_name} onChange={(e) => setUserFactoryForm(f => ({...f, bank_name: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Account No</label>
                      <input type="text" value={userFactoryForm.account_number} onChange={(e) => setUserFactoryForm(f => ({...f, account_number: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Sort Code</label>
                      <input type="text" value={userFactoryForm.sort_code} onChange={(e) => setUserFactoryForm(f => ({...f, sort_code: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={userFactoryForm.cis_registered} onChange={(e) => setUserFactoryForm(f => ({...f, cis_registered: e.target.checked}))} className="rounded" />
                    <label className="text-sm text-gray-700">CIS Registered</label>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0 flex justify-end gap-3">
              <button onClick={() => setShowUserFactoryModal(false)} className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium">
                Close
              </button>
              <button
                onClick={handleUserFactorySubmit}
                disabled={userFactoryCreating || !userFactoryOrg || !userFactoryForm.first_name || !userFactoryForm.last_name || !userFactoryForm.password}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                data-testid="user-factory-submit"
              >
                {userFactoryCreating ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeploymentToolsPage;
